import {
  IonButton,
  IonButtons,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { useState } from "react";
import { AppHeader } from "../AppHeader";
import { useAcceptInvitation, useDeclineInvitation } from "@/hooks";
import type { OrganisationInvitation } from "@/types";

export interface AcceptInvitationModalProps {
  invitation: OrganisationInvitation | null;
  isOpen: boolean;
  onClose: (result?: { success: boolean; message: string }) => void;
}

export default function AcceptInvitationModal({
  invitation,
  isOpen,
  onClose,
}: AcceptInvitationModalProps) {
  const [action, setAction] = useState<"accept" | "decline">("accept");
  const [reason, setReason] = useState("");

  const acceptMutation = useAcceptInvitation();
  const declineMutation = useDeclineInvitation();

  const isPending = acceptMutation.isPending || declineMutation.isPending;

  const handleConfirm = async () => {
    if (!invitation) return;

    try {
      if (action === "accept") {
        await acceptMutation.mutateAsync(invitation.id);
        onClose({
          success: true,
          message: "Je bent nu lid van de organisatie.",
        });
      } else {
        await declineMutation.mutateAsync(invitation.id);
        onClose({ success: true, message: "Uitnodiging afgewezen." });
      }
    } catch {
      onClose({
        success: false,
        message: "Er ging iets mis. Probeer het opnieuw.",
      });
    }
  };

  const handleClose = () => {
    setAction("accept");
    setReason("");
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <AppHeader title="Uitnodiging" onClose={handleClose} />
      <IonContent className="ion-padding">
        <IonText>
          <p>
            Accepteer of weiger de uitnodiging voor{" "}
            {invitation?.organisation.name}
          </p>
        </IonText>

        {invitation?.description && (
          <IonItem className="ion-margin-top" lines="none">
            <IonLabel>
              <p
                style={{
                  fontStyle: "italic",
                  color: "var(--ion-color-medium)",
                }}
              >
                "{invitation.description}"
              </p>
            </IonLabel>
          </IonItem>
        )}

        <IonRadioGroup
          value={action}
          onIonChange={(e) => setAction(e.detail.value)}
        >
          <IonList lines="none">
            <IonItem>
              <IonRadio slot="start" value="accept" />
              <IonLabel>Accepteren</IonLabel>
            </IonItem>
            <IonItem>
              <IonRadio slot="start" value="decline" />
              <IonLabel>Weigeren</IonLabel>
            </IonItem>
          </IonList>
        </IonRadioGroup>

        {action === "decline" && (
          <IonItem className="ion-margin-top">
            <IonLabel position="stacked">Reden (optioneel)</IonLabel>
            <IonTextarea
              value={reason}
              onIonInput={(e) => setReason(e.detail.value || "")}
              placeholder="Geef optioneel een reden op..."
              rows={3}
            />
          </IonItem>
        )}

        <IonButtons className="ion-margin-top ion-justify-content-end">
          <IonButton fill="clear" onClick={handleClose} disabled={isPending}>
            Sluiten
          </IonButton>
          <IonButton fill="solid" onClick={handleConfirm} disabled={isPending}>
            {isPending ? <IonSpinner name="crescent" /> : "Bevestigen"}
          </IonButton>
        </IonButtons>
      </IonContent>
    </IonModal>
  );
}
